interface ErrorWithMessage {
  message: string;
  code: string;
  field?: string;
}

export interface ParsedError {
  path: string | undefined;
  message: string;
}

export const isErrorWithMessage = (error: unknown): error is ErrorWithMessage => {
  return typeof error === 'object' && error !== null && 'message' in error && 'code' in error;
};

/**
 * Extracts the path and message from a structured error message string.
 *
 * The input error message is expected to follow a format where 'Path:' and 'Message:'
 * are delimiters for the actual path and message content.
 *
 * @param errorMessage - The error message string to parse.
 * @returns An object containing the extracted path and message, or null if parsing fails.
 */
function parseErrorMessage(errorMessage: string): ParsedError | null {
  const pathAndMessageRegex = /Path:\s*([\S\s]*?)\s*Message:\s*([\S\s]*?)(?:\s*Line:|$)/;

  const match = errorMessage.match(pathAndMessageRegex);

  if (match && match[1] && match[2]) {
    return {
      path: match[1].trim().replace(/\.$/, ''), // Remove trailing period from path
      message: match[2].trim(),
    };
  }

  // For non-standard format messages, return with null path so they're still processed
  return {
    message: errorMessage,
    path: undefined,
  };
}

/**
 * Processes an array of error objects, extracting a path and message from each.
 *
 * Each error object is expected to have a 'message' property containing a string
 * with "Path: " and "Message: " delimiters.
 *
 * @param errors - An array of error objects, each with a 'message' property.
 * @returns An array of objects, each containing 'path' and 'message' properties.
 *          Returns an empty array if the input is null, undefined, or empty.
 */
export function extractPathAndMessageFromErrors(errors: ErrorWithMessage[] | undefined | null): ParsedError[] {
  if (!errors || errors.length === 0) {
    return [];
  }

  const parsedErrors: ParsedError[] = [];

  for (const error of errors) {
    if (isErrorWithMessage(error)) {
      if (error.field) {
        parsedErrors.push({ path: error.field, message: error.message });
        continue;
      }
      const parsed = parseErrorMessage(error.message);
      if (parsed) {
        parsedErrors.push(parsed);
      }
    }
  }

  return parsedErrors;
}
